import { api } from './client'

export interface ActivityEntry {
  eventType: string
  description: string
  occurredAt: string
  entityId: string | null
  /** F-109: id della riga del ledger (per il dettaglio prima/dopo). */
  id?: number
  module?: string | null
  entityType?: string | null
  /** F-109: motivazione della modifica (header X-Change-Reason), se c'e'. */
  motivation?: string | null
}

/** F-109 (REQ-184-001, REQ-295-001): dettaglio di una riga del ledger. Le chiavi di importo mancano del tutto
 * (non sono null) se il chiamante non ha `CanSeeRealAmounts`: `amountsHidden` lo dichiara. */
export interface AuditEntryDetail {
  id: number
  module: string
  entityType: string
  entityId: string | null
  action: string
  origin: string
  actorUserId: number | null
  actorDisplay: string | null
  occurredAt: string
  motivation: string | null
  amountsHidden: boolean
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}

export interface AuditActivity {
  total: number
  items: ActivityEntry[]
}

export interface LoginEntry {
  id: number
  name: string
  email: string
  role: string
  lastLoginAt: string
  loginAttempts: number
}

/**
 * TASK-0099: mirrors the six optional query parameters `AuditController.RecentActivity`
 * (TASK-0096) added on top of the pre-existing `limit` — all AND-composable filters applied
 * server-side, see the controller's remarks for the exact semantics of each
 * (`actorUserId`/`action`/`ipAddress`/`entityType`/`entityId` are exact matches, `from`/`to` an
 * inclusive `OccurredAt` range). `entityType`/`entityId` are kept here for completeness even
 * though `AuditPage.tsx` does not currently expose controls for them (see its comments) — a
 * future caller gets the same client-side guard `activity()` applies below.
 */
export interface AuditActivityFilters {
  limit?: number
  actorUserId?: number
  action?: string
  entityType?: string
  entityId?: string
  ipAddress?: string
  /** `<input type="date">` value ("YYYY-MM-DD"). Sent as-is: a bare date parses to local midnight,
   * which is exactly the inclusive lower bound `from` wants (`OccurredAt >= from`). */
  from?: string
  /** `<input type="date">` value ("YYYY-MM-DD"). Unlike `StatsController`'s `dateTo`,
   * `AuditController.RecentActivity` does NOT special-case a bare date as "the whole day" — it
   * compares `OccurredAt <= to` directly against whatever `DateTime` model binding produces, so a
   * bare "YYYY-MM-DD" would bind to local midnight and silently exclude every event that actually
   * happened *on* that day (an operator picking "to: today" would see nothing from today).
   * `activity()` below appends `T23:59:59.999` before sending so the filter behaves the way the
   * UI's label promises. This is a client-side formatting choice only — no backend change. */
  to?: string
  /** F-109: solo righe con (true) o senza (false) motivazione. */
  hasMotivation?: boolean
}

export const auditApi = {
  /**
   * `GET /api/audit/activity`. No filters (besides the default `limit`) → identical request to
   * the pre-TASK-0099 call (`limit=100`, most recent rows first), so existing behavior is
   * unchanged when the operator never touches the new filter bar.
   */
  activity: (filters: AuditActivityFilters = {}) => {
    // Same "entityId requires entityType" rule `AuditController.RecentActivity` enforces
    // server-side with a 400 — replicated here so a caller fails fast locally instead of
    // round-tripping a request guaranteed to be rejected.
    if (filters.entityId && !filters.entityType) {
      throw new Error('auditApi.activity: entityId requires entityType')
    }
    const p = new URLSearchParams()
    p.set('limit', String(filters.limit ?? 100))
    if (filters.actorUserId != null) p.set('actorUserId', String(filters.actorUserId))
    if (filters.action) p.set('action', filters.action)
    if (filters.entityType) p.set('entityType', filters.entityType)
    if (filters.entityId) p.set('entityId', filters.entityId)
    if (filters.ipAddress) p.set('ipAddress', filters.ipAddress)
    if (filters.from) p.set('from', filters.from)
    if (filters.to) p.set('to', `${filters.to}T23:59:59.999`)
    if (filters.hasMotivation !== undefined) p.set('hasMotivation', String(filters.hasMotivation))
    return api<AuditActivity>(`/api/audit/activity?${p}`)
  },
  /** F-109: `GET /api/audit/entries/{id}` (Admin): before/after e motivazione di una riga. */
  entry: (id: number) => api<AuditEntryDetail>(`/api/audit/entries/${id}`),
  logins: (page = 1, size = 25) =>
    api<{ total: number; page: number; size: number; items: LoginEntry[] }>(
      `/api/audit/logins?page=${page}&size=${size}`
    ),
  lockedUsers: () =>
    api<{ total: number; items: Array<{ id: number; name: string; email: string; loginAttempts: number; lockedUntil: string }> }>(
      '/api/audit/locked-users'
    ),
}
